import { Injectable } from '@angular/core';
import { MessageService } from '@services/helpers/message-service/message.service';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { PaymentsServiceProvider } from '@services/payments/payments-service/payments.service';
import { Product } from '@models/models';

interface Action {
	readonly value: string;
	readonly label: string;
	readonly type: string;
}

@Injectable()
export class InvoiceServiceProvider {
	constructor(
		private messageService: MessageService,
		private timeService: TimeServiceProvider,
		private paymentsService: PaymentsServiceProvider
	) {}

	handleErrorGetInvoice(errorMessage = ''): void {
		if (errorMessage) {
			this.messageService.showToaster('e_2', 'general', errorMessage);
		} else {
			this.messageService.showToaster('e_0', 'general');
		}
	}

	errorGetInvoiceInformation({ data, status }): void {
		const errorMessage = data ? data.errorMessage : null;
		if (status >= 400 && status < 500 && errorMessage) {
			if (errorMessage.includes('not found')) {
				this.messageService.showToaster('e_5', 'editInvoice');
				return;
			}
			this.messageService.showToaster('e_2', 'general', errorMessage);
		} else {
			this.messageService.showToaster('e_0', 'general');
		}
	}

	setFormattedDaysToDue(items): Array<any> {
		items.forEach(invoice => {
			const dayMulti = this.addDayMulti(invoice.daysToDue);

			if (invoice.daysToDue === 0) {
				invoice.formattedDaysToDue = 'today';
			} else if (invoice.daysToDue > 0) {
				invoice.formattedDaysToDue = `in ${invoice.daysToDue + dayMulti}`;
			} else {
				invoice.formattedDaysToDue = `${Math.abs(
					invoice.daysToDue
				)}${dayMulti} ago`;
			}
		});

		return items;
	}

	isInvalidProduct(product, pageName: string): boolean {
		const messagePlace =
			pageName === 'invoicePage' ? 'editInvoice' : 'editQuote';

		if (
			product.type !== 'GIFT' &&
			(!product.description ||
				product.description.length === 0 ||
				product.description === '<p></p>')
		) {
			this.messageService.showToaster('e_12', messagePlace);
			return true;
		}

		return false;
	}

	errorMarkAsPaid({ status, data }): void {
		const errorMessage = data ? data.errorMessage : null;
		let errorCode, errorPlace, value;

		if (status >= 400 && status < 500 && errorMessage) {
			if (errorMessage === 'Payment already paid') {
				errorCode = 'e_10';
				errorPlace = 'editInvoice';
			} else if (
				errorMessage === "Xero payments don't match StudioNinja payments"
			) {
				errorCode = 'e_0';
				errorPlace = 'xero';
			} else if (errorMessage === 'Invoice not synchronized with Xero') {
				errorCode = 'e_9';
				errorPlace = 'xero';
			} else if (
				errorMessage.includes(
					'Xero access token expired, reconnect Xero please'
				) ||
				errorMessage.includes(
					'Bummer! Xero access token not authorized or revoked, reconnect Xero please'
				) ||
				errorMessage.includes(
					'Bummer! Xero access token revoked, reconnect Xero please'
				)
			) {
				errorCode = 'e_14';
				errorPlace = 'xero';
			} else if (
				errorMessage.includes(
					"Bummer! You don't have an active Xero subscription."
				)
			) {
				errorCode = 'e_15';
				errorPlace = 'xero';
			} else {
				errorCode = 'e_2';
				errorPlace = 'general';
				value = errorMessage;
			}
		} else {
			errorCode = 'e_0';
			errorPlace = 'general';
		}
		this.messageService.showToaster(errorCode, errorPlace, value);
	}

	errorSetUnpaid({ status, data }): void {
		const errorMessage = data ? data.errorMessage : null;
		let errorCode, errorPlace, value;

		if (status >= 400 && status < 500 && errorMessage) {
			if (
				errorMessage.includes(
					'This payment cannot be marked as unpaid because it was overpaid in Xero.'
				)
			) {
				errorCode = 'e_11';
				errorPlace = 'xero';
			} else {
				errorCode = 'e_2';
				errorPlace = 'general';
				value = errorMessage;
			}
		} else {
			errorCode = 'e_0';
			errorPlace = 'general';
		}
		this.messageService.showToaster(errorCode, errorPlace, value);
	}

	errorDeleteInvoice({ status, data }): void {
		const errorMessage = data ? data.errorMessage : null;
		let errorCode, errorPlace, value;

		if (status >= 400 && status < 500 && errorMessage) {
			if (
				errorMessage.includes(
					'This payment cannot be marked as unpaid because it was overpaid in Xero.'
				)
			) {
				errorCode = 'e_12';
				errorPlace = 'xero';
				value = 'invoice';
			} else if (
				errorMessage.includes(
					'Xero access token expired, reconnect Xero please'
				) ||
				errorMessage.includes(
					'Bummer! Xero access token not authorized or revoked, reconnect Xero please'
				) ||
				errorMessage.includes(
					'Bummer! Xero access token revoked, reconnect Xero please'
				)
			) {
				errorCode = 'e_14';
				errorPlace = 'xero';
			} else if (
				errorMessage.includes(
					"Bummer! You don't have an active Xero subscription."
				)
			) {
				errorCode = 'e_15';
				errorPlace = 'xero';
			} else {
				errorCode = 'e_2';
				errorPlace = 'general';
				value = errorMessage;
			}
		} else {
			errorCode = 'e_0';
			errorPlace = 'general';
		}
		this.messageService.showToaster(errorCode, errorPlace, value);
	}

	errorSaveInvoice(errorObject, invoiceId): void {
		const { error, status } = errorObject;
		const errorMessage = error ? error.errorMessage : null;
		const errorTitle = error ? error.errorTitle : null;

		let errorCode, errorPlace, value;
		if (errorMessage && status >= 400 && status < 500) {
			if (errorTitle && errorMessage) {
				this.messageService.showErrorFromBackend(errorTitle, errorMessage);
			} else {
				if (
					errorMessage ===
					"Paid or partially paid invoice can't be updated on Xero"
				) {
					errorCode = 'e_5';
					errorPlace = 'xero';
				} else if (errorMessage.includes('already exists/existed')) {
					errorCode = 'e_8';
					errorPlace = 'editInvoice';
					value = invoiceId;
				} else if (
					errorMessage ===
					'You have reached the limit of invoices you can approve'
				) {
					errorCode = 'e_11';
					errorPlace = 'editInvoice';
				} else if (
					errorMessage === 'Fixed discount is not compatible with Xero'
				) {
					errorCode = 'e_1';
					errorPlace = 'xero';
				} else if (
					errorMessage ===
					'Product/Package tax type is not compatible with Xero'
				) {
					errorCode = 'e_4';
					errorPlace = 'xero';
				} else if (
					errorMessage ===
					'Invoice with such invoice ID (Invoice Number) exists on Xero'
				) {
					errorCode = 'e_6';
					errorPlace = 'xero';
					value = invoiceId;
				} else if (
					errorMessage ===
					'Invoice should have the same tax type for all products'
				) {
					errorCode = 'e_0';
					errorPlace = 'qb';
				} else if (
					errorMessage === "QuickBooks doesn't allow Inclusive tax for US users"
				) {
					errorCode = 'e_3';
					errorPlace = 'qb';
				} else if (
					errorMessage ===
					'QuickBooks access token has been revoked, connect QuickBooks again'
				) {
					errorCode = 'e_4';
					errorPlace = 'qb';
				} else if (
					errorMessage ===
					'Sorry, your invoice data contains characters incompatible with QuickBooks'
				) {
					errorCode = 'e_10';
					errorPlace = 'qb';
				} else if (
					errorMessage ===
					"We're sorry, QuickBooks encountered an error while calculating tax. Try reselecting the tax rate or reentering the product/service item and saving the form again."
				) {
					errorCode = 'e_9';
					errorPlace = 'qb';
				} else if (
					errorMessage.includes(
						'Email Address does not conform to the syntax rules of RFC 822'
					)
				) {
					errorCode = 'e_18';
					errorPlace = 'qb';
				} else if (
					errorMessage ===
					'QuickBooks customer linked to this invoice has been deleted'
				) {
					errorCode = 'e_20';
					errorPlace = 'qb';
				} else if (
					errorMessage ===
					'Fixed discounts with exclusive amounts are not allowed'
				) {
					errorCode = 'e_22';
					errorPlace = 'qb';
				} else if (
					errorMessage.includes(
						'Category with such name already exists on QuickBooks'
					)
				) {
					errorCode = 'e_24';
					errorPlace = 'qb';
				} else if (
					errorMessage.startsWith(
						'QB: Impossible to create invoice with Product'
					)
				) {
					errorCode = 'e_25';
					errorPlace = 'qb';
				} else if (
					errorMessage.includes(
						'String length specified does not match the supported length. Min:0 Max:100 supported.'
					)
				) {
					errorCode = 'e_26';
					errorPlace = 'qb';
				} else if (
					errorMessage.includes(
						'String length specified does not match the supported length. Min:0 Max:21 supported.'
					)
				) {
					errorCode = 'e_27';
					errorPlace = 'qb';
				} else if (
					errorMessage.includes(
						'String length specified does not match the supported length. Min:0 Max:4,000 supported.'
					)
				) {
					errorCode = 'e_28';
					errorPlace = 'qb';
				} else if (
					errorMessage.includes(
						'Xero access token expired, reconnect Xero please'
					) ||
					errorMessage.includes(
						'Bummer! Xero access token not authorized or revoked, reconnect Xero please'
					) ||
					errorMessage.includes(
						'Bummer! Xero access token revoked, reconnect Xero please'
					)
				) {
					errorCode = 'e_14';
					errorPlace = 'xero';
				} else if (
					errorMessage.includes("You don't have an active Xero subscription")
				) {
					errorCode = 'e_15';
					errorPlace = 'xero';
				} else if (
					errorMessage.includes(
						'This Invoice ID is already used. Please enter another one'
					)
				) {
					errorCode = 'e_13';
					errorPlace = 'editInvoice';
				} else if (
					errorMessage.includes(
						'You cannot change the total of this invoice to a smaller amount than has already been paid.'
					)
				) {
					errorCode = 'e_4';
					errorPlace = 'editInvoice';
				} else if (
					errorMessage.includes(
						'Invoice total amount should be more or equals then paid part of this invoice'
					)
				) {
					errorCode = 'e_4';
					errorPlace = 'editInvoice';
				} else {
					errorCode = 'e_2';
					errorPlace = 'general';
					value = errorMessage;
				}
				this.messageService.showToaster(errorCode, errorPlace, value);
			}
		} else {
			errorCode = 'e_0';
			errorPlace = 'general';
			this.messageService.showToaster(errorCode, errorPlace, value);
		}
	}

	errorWriteOff(data): void {
		if (
			data.errorMessage ===
			'Write off feature disabled for copied to QuickBooks invoices'
		) {
			this.messageService.showToaster('e_16', 'qb');
		} else {
			this.messageService.showDefaultErrorMessage(data);
		}
	}

	errorCopyToXero(error, invoiceDisplayId: string): void {
		const { data, status } = error;
		const errorMessage = data ? data.errorMessage : null;
		let errorCode, errorPlace, value;
		if (errorMessage && status >= 400 && status < 500) {
			switch (errorMessage) {
				case "Paid or partially paid invoice can't be updated on Xero":
					errorCode = 'e_5';
					errorPlace = 'xero';
					break;
				case 'Invoice with such invoice ID (Invoice Number) exists on Xero':
					errorCode = 'e_6';
					errorPlace = 'xero';
					value = invoiceDisplayId;
					break;
				case 'Product/Package tax type is not compatible with Xero':
					errorCode = 'e_4';
					errorPlace = 'xero';
					break;
				case 'Fixed discount is not compatible with Xero':
					errorCode = 'e_1';
					errorPlace = 'xero';
					break;
				case 'Invoice is Voided or Deleted on Xero':
					errorCode = 'e_3';
					errorPlace = 'xero';
					break;
				case 'Invoice with written off payments is not compatible with Xero':
					errorCode = 'e_7';
					errorPlace = 'xero';
					break;
				case 'Xero access token expired, reconnect Xero please':
					errorCode = 'e_14';
					errorPlace = 'xero';
					break;
				case 'Bummer! Xero access token not authorized or revoked, reconnect Xero please':
					errorCode = 'e_14';
					errorPlace = 'xero';
					break;
				case 'Bummer! Xero access token revoked, reconnect Xero please':
					errorCode = 'e_14';
					errorPlace = 'xero';
					break;
				case "Bummer! You don't have an active Xero subscription.":
					errorCode = 'e_15';
					errorPlace = 'xero';
					break;
				default:
					this.messageService.showErrorFromBackend(
						'Houston, we have a Xero problem!!'
					);
					return;
			}
		} else {
			this.messageService.showErrorFromBackend(
				'Houston, we have a Xero problem!!'
			);
			return;
		}
		this.messageService.showToaster(errorCode, errorPlace, value);
	}

	errorCopyToQuickbooks(error, invoiceDisplayId: string) {
		const { data, status } = error;
		const errorMessage = data ? data.errorMessage : null;
		const { errorTitle } = error.error;
		let errorCode, errorPlace, value;
		if (errorMessage && status >= 400 && status < 500) {
			switch (errorMessage) {
				case "QuickBooks doesn't allow Inclusive tax for US users":
					errorCode = 'e_3';
					errorPlace = 'qb';
					break;
				case 'Invoice with written off payments is not compatible with QuickBooks':
					errorCode = 'e_7';
					errorPlace = 'qb';
					break;
				case 'Sorry, FR QuickBooks companies are not supported yet':
					errorCode = 'e_8';
					errorPlace = 'qb';
					break;
				case 'Invoice with such ID already exists on QuickBooks':
					errorCode = 'e_1';
					errorPlace = 'qb';
					value = invoiceDisplayId;
					break;
				case 'Invoice with written off payments is not compatible with Xero':
					errorCode = 'e_7';
					errorPlace = 'xero';
					break;
				case 'Invoice has been deleted deleted on QuickBooks':
					errorCode = 'e_17';
					errorPlace = 'qb';
					break;
				case 'Invoice should have the same tax type for all products':
					errorCode = 'e_0';
					errorPlace = 'qb';
					break;
				case "StudioNinja doesn't support transactions with Credit Notes from QuickBooks":
					errorCode = 'e_14';
					errorPlace = 'qb';
					break;
				default:
					if (
						errorTitle &&
						errorTitle !== null &&
						errorMessage &&
						errorMessage !== null
					) {
						this.messageService.showErrorFromBackend(errorTitle, errorMessage);
					} else {
						this.messageService.showErrorFromBackend(
							'Slow down, Quickbooks error!'
						);
					}
					return;
			}

			if (
				errorMessage.includes(
					'String length specified does not match the supported length. Min:0 Max:100 supported.'
				)
			) {
				errorCode = 'e_26';
				errorPlace = 'qb';
			} else if (
				errorMessage.includes(
					'String length specified does not match the supported length. Min:0 Max:21 supported.'
				)
			) {
				errorCode = 'e_27';
				errorPlace = 'qb';
			} else if (
				errorMessage.includes(
					'String length specified does not match the supported length. Min:0 Max:4,000 supported.'
				)
			) {
				errorCode = 'e_28';
				errorPlace = 'qb';
			} else if (
				errorMessage.includes(
					'One of the list elements assigned to this transaction has been deleted'
				)
			) {
				errorCode = 'e_11';
				errorPlace = 'qb';
			} else if (
				errorMessage.includes(
					'Try reselecting the tax rate or reentering the product/service item and saving the form again.'
				)
			) {
				errorCode = 'e_19';
				errorPlace = 'qb';
			}
		} else {
			if (
				errorTitle &&
				errorTitle !== null &&
				errorMessage &&
				errorMessage !== null
			) {
				this.messageService.showErrorFromBackend(errorTitle, errorMessage);
			} else {
				this.messageService.showErrorFromBackend(
					'Slow down, Quickbooks error!'
				);
			}
			return;
		}
		this.messageService.showToaster(errorCode, errorPlace, value);
	}

	chooseActionsMenuToShow(payment): Array<Action> {
		if (payment.writtenOff) {
			return this.getWrittenOffPaymentActions();
		} else if (payment.paid) {
			return this.getPaidPaymentActions();
		} else if (payment.xeroEnabled || payment.qbEnabled) {
			return this.getIntegratedPaymentActions();
		} else if (!payment.paid) {
			return this.getUnpaidPaymentActions();
		}
		return null;
	}

	isPaymentToRecordValid(amount: number, invoice: any): boolean {
		if (amount <= 0 && invoice.xeroStatus === 'COPIED_TO_XERO') {
			this.messageService.showToaster('e_8', 'xero');
			return false;
		} else if (amount <= 0 && invoice.qbStatus === 'COPIED') {
			this.messageService.showToaster('e_8', 'xero');
			return false;
		}

		return true;
	}

	isSetPaidPaymentError(payment): boolean {
		if (
			payment.errorMessage ===
			"StudioNinja doesn't support transactions with Credit Notes from QuickBooks"
		) {
			this.messageService.showToaster('e_14', 'qb');
			return true;
		} else if (
			payment.errorMessage ===
			'"One of the list elements assigned to this transaction has been deleted'
		) {
			this.messageService.showToaster('e_11', 'qb');
			return true;
		}
		return false;
	}

	isSetUnpaidPaymentError(invoice): boolean {
		if (invoice.qbStatus === 'DISCONNECTED_INVOICE_ALTERED') {
			this.messageService.showToaster('e_14', 'qb');
			return true;
		}
		return false;
	}

	isInvoiceValid(data): boolean {
		const {
			amount,
			packages,
			payments,
			discount,
			discountType,
			totalDue,
			poNumber,
			pageName
		} = data;

		let invalidProductType;

		const messagePlace =
			pageName === 'invoicePage' ? 'editInvoice' : 'editQuote';

		if (this.isChosenProductsInvalid(packages, pageName)) {
			return false;
		}

		if (discountType === 'Fixed' && amount - discount < 0) {
			this.messageService.showToaster('e_1', 'editInvoice');
			return false;
		} else if (this.paymentsService.isNegativePayments(payments)) {
			this.messageService.showToaster('e_4', 'editInvoice');
			return false;
		}

		const isValidDiscountFixed = this.paymentsService.isValidDiscountFixed(
			packages,
			discountType
		);
		if (!isValidDiscountFixed) {
			return false;
		}

		const isValidDiscountPercent = this.paymentsService.isValidDiscountPercent(
			discount,
			discountType,
			pageName
		);
		if (!isValidDiscountPercent) {
			return false;
		}

		if (this.paymentsService.isPaymentsWithoutDate(payments)) {
			this.messageService.showToaster('e_9', 'editInvoice');
			return false;
		}

		if (typeof totalDue === 'undefined' || totalDue === null) {
			this.messageService.showToaster('e_0', 'general');
			return false;
		}

		if (
			poNumber &&
			poNumber !== '' &&
			poNumber !== null &&
			!poNumber.match('^[a-zA-Z0-9 *№\'ʼ;:%!?@#$&|>\\<{}()-`.+,/"]*$')
		) {
			this.messageService.showToaster('e_7', 'editQuote');
			return false;
		}

		if (totalDue < 0) {
			this.messageService.showToaster('e_1', messagePlace);
			return false;
		}
		if (
			packages.find(
				product => product.type === 'GIFT' && product.price >= 0
			) !== undefined
		) {
			this.messageService.showToaster('e_16', 'editQuote');
			return false;
		}
		if (
			packages.find(product => {
				if (product.name === '') {
					invalidProductType = product.type;
					if (invalidProductType === 'GIFT') {
						invalidProductType = 'discount';
					} else if (invalidProductType === 'ADD_ON') {
						invalidProductType = 'product';
					}
					return true;
				}
			})
		) {
			this.messageService.showToaster(
				'e_0',
				'productsPackages',
				invalidProductType.toLowerCase()
			);
			return false;
		}
		if (
			packages.find(product => {
				if (product.name.trim().length === 0) {
					if (product.type.toUpperCase() === 'GIFT') {
						invalidProductType = 'Discount';
					} else {
						invalidProductType =
							product.type.toUpperCase() === 'PACKAGE' ? 'Package' : 'Product';
					}
					return true;
				}
			})
		) {
			this.messageService.showToaster('e_14', 'editQuote', invalidProductType);
			return false;
		}
		// MAGIC DON'T TOUCH
		if (
			packages.filter(addon => addon.type === 'GIFT').length > 0 &&
			packages.filter(addon => addon.taxType !== packages[0].taxType).length > 0
		) {
			this.messageService.showToaster('e_4', 'payments');
			return false;
		}

		return true;
	}

	private isChosenProductsInvalid(products, pageName: string): boolean {
		if (products.length === 0) {
			this.messageService.showToaster('w_2', 'editInvoice');
			return true;
		}
		return products.some(product => this.isInvalidProduct(product, pageName));
	}

	private addDayMulti(day: number): string {
		return ` day${Math.abs(day) === 1 ? '' : 's'}`;
	}

	private getUnpaidPaymentActions(): Array<Action> {
		return [
			{
				value: 'record',
				label: 'Record payment',
				type: 'radio'
			},
			{
				value: 'pay',
				label: 'Pay online',
				type: 'radio'
			},
			{
				value: 'writeoff',
				label: 'Write off',
				type: 'radio'
			}
		];
	}

	private getPaidPaymentActions(): Array<Action> {
		return [
			{
				value: 'unpaid',
				label: 'Mark as unpaid',
				type: 'radio'
			},
			{
				value: 'sendReceipt',
				label: 'Send receipt',
				type: 'radio'
			}
		];
	}

	private getIntegratedPaymentActions(): Array<Action> {
		return [
			{
				value: 'record',
				label: 'Record payment',
				type: 'radio'
			},
			{
				value: 'pay',
				label: 'Pay online',
				type: 'radio'
			}
		];
	}

	private getWrittenOffPaymentActions(): Array<Action> {
		return [
			{
				value: 'undoWriteoff',
				label: 'Undo',
				type: 'radio'
			}
		];
	}

	getInvoiceStampImageSrc(isSent: boolean): string {
		return `https://app.studioninja.co/build/release-pipeline-10/resources/dist/assets/images/icons/statuses/${
			isSent ? 'sent.png' : 'notsent.png'
		}`;
	}

	checkProductPackagesCorrectType(products: Array<Product>): Array<Product> {
		const paymentsProductPackage = JSON.parse(JSON.stringify(products));

		paymentsProductPackage.forEach(payment => {
			if (!payment.type || payment.type === null) {
				if (payment.price < 0) {
					payment.type = 'GIFT';
				} else {
					payment.type = 'ADD_ON';
				}
			} else if (
				payment.price > 0 &&
				(payment.type !== 'ADD_ON' || payment.type !== 'PACKAGE')
			) {
				payment.type = 'ADD_ON';
			}
		});

		return paymentsProductPackage;
	}

	successCreateInvoice(data) {
		return new Promise((resolve, reject) => {
			const { errorMessage, invoiceDisplayId, id, xeroResult } = data;
			if (errorMessage) {
				this.messageService.showToaster('e_2', 'general', errorMessage);
				reject();
			} else {
				this.messageService.showToaster('s_1', 'payments');

				if (xeroResult) {
					this.messageService.showToaster('e_2', 'xero');
					reject();
				}

				const metadata = {
					invoice_id: invoiceDisplayId
				};
				(<any>window).Intercom('trackEvent', 'created invoice', metadata);

				resolve(id);
			}
		});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=ls9lvp2s4pi!./shared/services/invoice/invoice-service/invoice.service.ts